import React, { useState } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import { Calendar, Clock, MapPin, Users, CheckCircle, Circle, ClipboardCheck, X, Edit2 } from 'lucide-react';
import AddEventModal from '../../../events/components/AddEventModal';
import { resolveMediaUrl } from '../../../utils/helpers';

// Mock schedule instances with RSVP data
const mockScheduleInstances = [
  {
    id: 1,
    date: '2026-02-24',
    dayOfWeek: 'Monday',
    time: '18:00',
    duration: 90,
    facility: 'Court A',
    status: 'completed',
    attendees: 14,
    capacity: 20
  },
  {
    id: 2,
    date: '2026-02-26',
    dayOfWeek: 'Wednesday',
    time: '18:00',
    duration: 90,
    facility: 'Court A',
    status: 'completed',
    attendees: 15,
    capacity: 20
  },
  {
    id: 3,
    date: '2026-02-28',
    dayOfWeek: 'Friday',
    time: '18:00',
    duration: 90,
    facility: 'Court A',
    status: 'completed',
    attendees: 13,
    capacity: 20
  },
  {
    id: 4,
    date: '2026-03-03',
    dayOfWeek: 'Monday',
    time: '18:00',
    duration: 90,
    facility: 'Court A',
    status: 'upcoming',
    rsvp: {
      available: 2,
      tentative: 1,
      notAvailable: 1,
      noResponse: 0
    },
    studentRSVPs: [
      { userId: 1, name: 'You (Current User)', status: 'available', profileImage: 'https://i.pravatar.cc/150?img=1' },
      { userId: 201, name: 'Alex Martinez', status: 'available', profileImage: 'https://i.pravatar.cc/150?img=11' },
      { userId: 202, name: 'Emma Chen', status: 'tentative', profileImage: 'https://i.pravatar.cc/150?img=12' },
      { userId: 203, name: 'James Wilson', status: 'notAvailable', profileImage: 'https://i.pravatar.cc/150?img=13' }
    ]
  },
  {
    id: 5,
    date: '2026-03-05',
    dayOfWeek: 'Wednesday',
    time: '18:00',
    duration: 90,
    facility: 'Court A',
    status: 'upcoming',
    rsvp: {
      available: 3,
      tentative: 0,
      notAvailable: 0,
      noResponse: 1
    },
    studentRSVPs: [
      { userId: 1, name: 'You (Current User)', status: 'available', profileImage: 'https://i.pravatar.cc/150?img=1' },
      { userId: 201, name: 'Alex Martinez', status: 'available', profileImage: 'https://i.pravatar.cc/150?img=11' },
      { userId: 202, name: 'Emma Chen', status: 'available', profileImage: 'https://i.pravatar.cc/150?img=12' },
      { userId: 203, name: 'James Wilson', status: 'noResponse', profileImage: 'https://i.pravatar.cc/150?img=13' }
    ]
  },
  {
    id: 6,
    date: '2026-03-07',
    dayOfWeek: 'Friday',
    time: '18:00',
    duration: 90,
    facility: 'Court A',
    status: 'upcoming',
    rsvp: {
      available: 1,
      tentative: 2,
      notAvailable: 1,
      noResponse: 0
    },
    studentRSVPs: [
      { userId: 1, name: 'You (Current User)', status: 'tentative', profileImage: 'https://i.pravatar.cc/150?img=1' },
      { userId: 201, name: 'Alex Martinez', status: 'available', profileImage: 'https://i.pravatar.cc/150?img=11' },
      { userId: 202, name: 'Emma Chen', status: 'tentative', profileImage: 'https://i.pravatar.cc/150?img=12' },
      { userId: 203, name: 'James Wilson', status: 'notAvailable', profileImage: 'https://i.pravatar.cc/150?img=13' }
    ]
  }
];

const ClassScheduleTab = ({ classId, classData }) => {
  const { theme } = useTheme();
  const [showPastSessions, setShowPastSessions] = useState(false);
  const [showAttendanceModal, setShowAttendanceModal] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  const [scheduleInstances, setScheduleInstances] = useState(mockScheduleInstances);

  // Mock students for attendance (in real app, fetch from classData)
  const mockStudents = [
    {
      userId: 201,
      firstName: 'Alex',
      lastName: 'Martinez',
      profileImage: 'https://i.pravatar.cc/150?img=11',
      attendanceRate: 92.5
    },
    {
      userId: 202,
      firstName: 'Emma',
      lastName: 'Chen',
      profileImage: 'https://i.pravatar.cc/150?img=12',
      attendanceRate: 100
    },
    {
      userId: 203,
      firstName: 'James',
      lastName: 'Wilson',
      profileImage: 'https://i.pravatar.cc/150?img=13',
      attendanceRate: 88.0
    }
  ];

  const handleMarkAttendance = (session) => {
    setSelectedSession(session);
    setShowAttendanceModal(true);
  };

  const handleSaveAttendance = (sessionId, attendanceData) => {
    console.log('Saving attendance for session:', sessionId, attendanceData);
    // In real app, this would call API to save attendance
    setShowAttendanceModal(false);
  };

  const handleEditSession = (session) => {
    // Convert session format to event format for AddEventModal
    const eventData = {
      subject: `${classData.className} - Class Session`,
      address: session.facility,
      date: session.date,
      startTime: session.time,
      // Calculate end time from duration
      endTime: calculateEndTime(session.time, session.duration)
    };

    setEditingSession({ ...session, eventData });
    setShowEditModal(true);
  };

  const calculateEndTime = (startTime, durationMinutes) => {
    const [hours, minutes] = startTime.split(':').map(Number);
    const totalMinutes = hours * 60 + minutes + durationMinutes;
    const endHours = Math.floor(totalMinutes / 60) % 24;
    const endMinutes = totalMinutes % 60;
    return `${String(endHours).padStart(2, '0')}:${String(endMinutes).padStart(2, '0')}`;
  };

  const calculateDuration = (startTime, endTime) => {
    const [startHours, startMinutes] = startTime.split(':').map(Number);
    const [endHours, endMinutes] = endTime.split(':').map(Number);
    const startTotalMinutes = startHours * 60 + startMinutes;
    const endTotalMinutes = endHours * 60 + endMinutes;
    return endTotalMinutes - startTotalMinutes;
  };

  const handleSaveSession = (eventData) => {
    console.log('Saving event data:', eventData);

    // Convert event format back to session format
    const updatedSession = {
      ...editingSession,
      date: eventData.date,
      dayOfWeek: new Date(eventData.date).toLocaleDateString('en-US', { weekday: 'long' }),
      time: eventData.startTime,
      duration: calculateDuration(eventData.startTime, eventData.endTime),
      facility: eventData.address
    };

    // Update the session in the list
    setScheduleInstances(prev =>
      prev.map(session =>
        session.id === editingSession.id ? updatedSession : session
      )
    );

    setShowEditModal(false);
    setEditingSession(null);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const isToday = (dateStr) => {
    const today = new Date().toISOString().split('T')[0];
    return dateStr === today;
  };

  const filteredInstances = showPastSessions
    ? scheduleInstances
    : scheduleInstances.filter(inst => inst.status === 'upcoming');

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h3 className={`text-xl font-bold ${theme.text}`}>Class Schedule</h3>
          <p className={theme.textSecondary}>
            {classData.recurrence} at {classData.time}
          </p>
        </div>
        <label className="flex items-center gap-2 cursor-pointer px-4 py-2 border rounded-lg hover:bg-gray-50 transition-colors">
          <input
            type="checkbox"
            checked={showPastSessions}
            onChange={(e) => setShowPastSessions(e.target.checked)}
            className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
          />
          <span className={theme.text}>Show Past Sessions</span>
        </label>
      </div>

      <div className="space-y-4">
        {filteredInstances.map(instance => (
          <div
            key={instance.id}
            className={`${theme.card} p-6 rounded-xl border ${theme.border} ${
              instance.status === 'completed' ? 'opacity-60' : ''
            } ${isToday(instance.date) ? 'ring-2 ring-blue-500' : ''}`}
          >
            <div className="flex items-start justify-between">
              {/* Left: Session Info */}
              <div className="flex items-start gap-4 flex-1">
                {/* Status Icon */}
                <div className="mt-1">
                  {instance.status === 'completed' ? (
                    <CheckCircle size={24} className="text-green-600" />
                  ) : (
                    <Circle size={24} className="text-blue-500" />
                  )}
                </div>

                {/* Details */}
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h4 className={`text-lg font-semibold ${theme.text}`}>
                      {instance.dayOfWeek}, {formatDate(instance.date)}
                    </h4>
                    {isToday(instance.date) && (
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-semibold">
                        TODAY
                      </span>
                    )}
                    {instance.status === 'completed' && (
                      <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-semibold">
                        COMPLETED
                      </span>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                    <div className="flex items-center">
                      <Clock size={16} className={`mr-2 ${theme.textSecondary}`} />
                      <span>{instance.time} ({instance.duration} min)</span>
                    </div>
                    <div className="flex items-center">
                      <MapPin size={16} className={`mr-2 ${theme.textSecondary}`} />
                      <span>{instance.facility}</span>
                    </div>
                    <div className="flex items-center">
                      <Users size={16} className={`mr-2 ${theme.textSecondary}`} />
                      <span>
                        {instance.status === 'completed'
                          ? `${instance.attendees} attended`
                          : `${instance.capacity} capacity`
                        }
                      </span>
                    </div>
                  </div>

                  {/* Attendance Progress Bar (for completed sessions) */}
                  {instance.status === 'completed' && (
                    <div className="mt-3">
                      <div className="flex justify-between text-xs mb-1">
                        <span className={theme.textSecondary}>Attendance</span>
                        <span>{instance.attendees}/{instance.capacity}</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-green-600 h-2 rounded-full"
                          style={{ width: `${(instance.attendees / instance.capacity) * 100}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Right: RSVP Status or Actions */}
              {instance.status === 'upcoming' && instance.rsvp && (
                <div className="flex flex-col gap-2 min-w-[200px]">
                  {/* RSVP Summary */}
                  <div className="grid grid-cols-2 gap-2">
                    {instance.rsvp.available > 0 && (
                      <div className="flex items-center gap-1 text-sm">
                        <div className="w-2 h-2 rounded-full bg-green-500"></div>
                        <span className="font-semibold text-green-700">{instance.rsvp.available}</span>
                        <span className={theme.textSecondary}>Available</span>
                      </div>
                    )}
                    {instance.rsvp.tentative > 0 && (
                      <div className="flex items-center gap-1 text-sm">
                        <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
                        <span className="font-semibold text-yellow-700">{instance.rsvp.tentative}</span>
                        <span className={theme.textSecondary}>Tentative</span>
                      </div>
                    )}
                    {instance.rsvp.notAvailable > 0 && (
                      <div className="flex items-center gap-1 text-sm">
                        <div className="w-2 h-2 rounded-full bg-red-500"></div>
                        <span className="font-semibold text-red-700">{instance.rsvp.notAvailable}</span>
                        <span className={theme.textSecondary}>Not Available</span>
                      </div>
                    )}
                    {instance.rsvp.noResponse > 0 && (
                      <div className="flex items-center gap-1 text-sm">
                        <div className="w-2 h-2 rounded-full bg-gray-400"></div>
                        <span className="font-semibold text-gray-600">{instance.rsvp.noResponse}</span>
                        <span className={theme.textSecondary}>No Response</span>
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => handleMarkAttendance(instance)}
                      className={`px-3 py-2 border ${theme.border} rounded-lg hover:bg-gray-50 transition text-sm flex items-center justify-center gap-2`}
                    >
                      <Users size={14} />
                      View Students
                    </button>
                    <button
                      onClick={() => handleEditSession(instance)}
                      className={`px-3 py-2 border ${theme.border} rounded-lg hover:bg-gray-50 transition text-sm flex items-center justify-center gap-2`}
                    >
                      <Edit2 size={14} />
                      Edit
                    </button>
                  </div>
                </div>
              )}
              {instance.status === 'upcoming' && !instance.rsvp && (
                <div className="flex gap-2">
                  <button
                    onClick={() => handleMarkAttendance(instance)}
                    className={`px-4 py-2 ${theme.buttonPrimary} rounded-lg transition text-sm flex items-center gap-2`}
                  >
                    <ClipboardCheck size={16} />
                    Mark Attendance
                  </button>
                  <button
                    onClick={() => handleEditSession(instance)}
                    className={`px-4 py-2 border ${theme.border} rounded-lg hover:bg-gray-50 transition text-sm flex items-center gap-2`}
                  >
                    <Edit2 size={16} />
                    Edit
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {filteredInstances.length === 0 && (
        <div className={`${theme.card} text-center p-12 rounded-xl`}>
          <Calendar size={48} className="mx-auto mb-3 opacity-50" />
          <h5 className={`text-xl font-semibold ${theme.text} mb-2`}>No sessions found</h5>
          <p className={theme.textSecondary}>
            {showPastSessions
              ? 'No sessions available.'
              : 'All upcoming sessions will appear here.'}
          </p>
        </div>
      )}

      {/* Student RSVP/Attendance Modal */}
      {selectedSession && showAttendanceModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowAttendanceModal(false)}
        >
          <div
            className={`${theme.card} rounded-xl w-full max-w-2xl max-h-[80vh] overflow-y-auto`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="p-6 border-b border-gray-200 sticky top-0 bg-white dark:bg-gray-800 z-10">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className={`text-xl font-bold ${theme.text}`}>Student Availability</h3>
                  <p className={theme.textSecondary}>
                    {selectedSession.dayOfWeek}, {formatDate(selectedSession.date)} at {selectedSession.time}
                  </p>
                </div>
                <button
                  onClick={() => setShowAttendanceModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            {/* Student List */}
            <div className="p-6">
              {selectedSession.studentRSVPs && selectedSession.studentRSVPs.length > 0 ? (
                <div className="space-y-3">
                  {selectedSession.studentRSVPs.map(student => (
                    <div key={student.userId} className={`p-4 rounded-lg border-2 ${
                      student.status === 'available' ? 'border-green-500 bg-green-50 dark:bg-green-900/20' :
                      student.status === 'tentative' ? 'border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20' :
                      student.status === 'notAvailable' ? 'border-red-500 bg-red-50 dark:bg-red-900/20' :
                      'border-gray-300 bg-gray-50 dark:bg-gray-800'
                    }`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <img
                            src={resolveMediaUrl(student.profileImage)}
                            alt={student.name}
                            className="w-12 h-12 rounded-full object-cover"
                          />
                          <div>
                            <h4 className={`font-semibold ${theme.text}`}>{student.name}</h4>
                            <div className="flex items-center gap-2 mt-1">
                              <div className={`w-2 h-2 rounded-full ${
                                student.status === 'available' ? 'bg-green-500' :
                                student.status === 'tentative' ? 'bg-yellow-500' :
                                student.status === 'notAvailable' ? 'bg-red-500' :
                                'bg-gray-400'
                              }`}></div>
                              <span className={`text-sm font-semibold ${
                                student.status === 'available' ? 'text-green-700' :
                                student.status === 'tentative' ? 'text-yellow-700' :
                                student.status === 'notAvailable' ? 'text-red-700' :
                                'text-gray-600'
                              }`}>
                                {student.status === 'available' ? 'Available' :
                                 student.status === 'tentative' ? 'Tentative' :
                                 student.status === 'notAvailable' ? 'Not Available' :
                                 'No Response'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Users size={48} className="mx-auto mb-3 text-gray-300" />
                  <p className={theme.textSecondary}>No student responses yet</p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-gray-200 bg-gray-50 dark:bg-gray-800">
              <div className="flex justify-between items-center">
                <div className="flex gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                    <span className={theme.textSecondary}>Available: {selectedSession.rsvp?.available || 0}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                    <span className={theme.textSecondary}>Tentative: {selectedSession.rsvp?.tentative || 0}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-500"></div>
                    <span className={theme.textSecondary}>Not Available: {selectedSession.rsvp?.notAvailable || 0}</span>
                  </div>
                </div>
                <button
                  onClick={() => setShowAttendanceModal(false)}
                  className={`px-4 py-2 ${theme.buttonPrimary} rounded-lg`}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Session Modal */}
      <AddEventModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEditingSession(null);
        }}
        initialData={editingSession?.eventData}
        onSave={handleSaveSession}
      />
    </div>
  );
};

export default ClassScheduleTab;
